import { Trans, useTranslation } from "react-i18next";
import { RiArrowRightSLine } from "@remixicon/react";
import { Button } from "~/components/ui/button";
import { Link } from "~/components/ui/link";
import { LINE_BREAKS } from "~/components/ui/max-width-line-break";
import { Picture } from "~/components/ui/picture";
import type { ProductFleetBannerContent } from "~/data/products/types";
import { cn } from "~/lib/utils";
import { imageUrl } from "~/lib/image-url";

interface FleetBannerSectionProps {
  content: ProductFleetBannerContent;
  // Content keys live in whichever page's own i18n namespace called this from.
  namespace?: "products" | "home";
  className?: string;
  // Figma's tablet frame wants the horizontal/white-text treatment from md, not lg —
  // true for every current caller (home, PDP, products list); set false to keep the
  // stacked mobile layout through tablet.
  rowFromMd?: boolean;
}

// Plain public/ path, not a JS import — Picture needs real .avif/.webp siblings on disk.
const fleetBannerBg = imageUrl("/images/fleet-banner-bg.png");

export function FleetBannerSection({
  content,
  namespace = "products",
  className,
  rowFromMd = true,
}: FleetBannerSectionProps) {
  const { t } = useTranslation(namespace);

  return (
    <section className={cn("wrap py-10 lg:py-7 2xl:max-w-[1280px]!", className)}>
      <div
        className={cn(
          "relative flex flex-col items-start gap-6 overflow-hidden rounded-2xl px-4 py-8 lg:flex-row lg:items-center lg:justify-between lg:gap-[17px] lg:px-12 lg:py-6 2xl:px-[60px]",
          rowFromMd && "md:flex-row md:items-center md:justify-between md:gap-6 md:px-10 md:py-6",
        )}
      >
        {/* Wrapped so <picture>'s <source> children aren't flex items that add gaps before the text.
            Figma's crops are sized by width only, so the photo keeps its proportions as the banner grows. */}
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <Picture
            src={fleetBannerBg}
            alt=""
            className={cn(
              "absolute top-full left-[-17.12%] h-auto w-[134.69%] max-w-none translate-y-[-85%] sm:translate-y-[-75%] lg:top-1/2 lg:left-[-6.07%] lg:w-[108.67%] lg:translate-y-[-57.4%]",
              rowFromMd && "md:top-1/2 md:left-[-6.07%] md:w-[108.67%] md:translate-y-[-57.4%]",
            )}
          />
        </div>
        <div
          aria-hidden
          className={cn(
            "pointer-events-none absolute inset-0 bg-[rgba(11,26,55,0.8)] lg:bg-[rgba(11,26,55,0.7)]",
            rowFromMd && "md:bg-[rgba(11,26,55,0.7)]",
          )}
        />
        <div
          aria-hidden
          className={cn("pointer-events-none absolute inset-0 lg:hidden", rowFromMd && "md:hidden")}
          style={{
            backgroundImage:
              "linear-gradient(180deg, rgba(10, 39, 92, 0.85) 30.07%, rgba(10, 39, 92, 0) 105.23%)",
          }}
        />
        <div
          aria-hidden
          className={cn("pointer-events-none absolute inset-0 hidden lg:block", rowFromMd && "md:block")}
          style={{
            backgroundImage:
              "linear-gradient(90deg, rgba(10, 39, 92, 0.75) 2.6886%, rgba(10, 39, 92, 0) 54.062%)",
          }}
        />
        <div
          className={cn(
            "relative flex flex-col items-center gap-2 text-center lg:max-w-[951px] lg:flex-1 lg:items-start lg:text-left",
            rowFromMd && "md:max-w-[951px] md:flex-1 md:items-start md:text-left",
          )}
        >
          <h2 className={cn("font-display text-xl leading-[1.15] font-extrabold text-white lg:text-[32px]", rowFromMd && "md:text-[32px]")}>
            {t(content.headline)}
          </h2>
          <p className={cn("text-sm leading-5 text-neutral-100 lg:text-base lg:leading-[1.5]", rowFromMd && "md:text-base md:leading-[1.5]")}>
            <Trans t={t} i18nKey={content.description} components={LINE_BREAKS} />
          </p>
        </div>
        <Button
          variant="secondary-alt"
          size="sm"
          className={cn("relative h-11 w-full text-base leading-5 tracking-[0.16px] lg:h-14 lg:w-auto", rowFromMd && "md:h-14 md:w-auto")}
          iconRight={<RiArrowRightSLine className="size-5" />}
          asChild
        >
          <Link data-sid="fleet-banner-cta" to={content.ctaHref} aria-label={t(content.ariaLabel)}>
            {t(content.ctaLabel)}
          </Link>
        </Button>
      </div>
    </section>
  );
}
